/**
 * Hub — Peace Home Base, the in-app home/landing surface (X.5.1; restyled
 * X.6.3 to the design-refresh "Warm" layout).
 *
 * Warm is the locked default (Amanda): a full-bleed forest hero band (gold
 * eyebrow + time-of-day icon + date, Playfair greeting, today's quote) over a
 * two-column body — left: "Jump back in" recent tools + the capability-gated
 * tool sections; right (sticky): the "Around the office" announcements card.
 * The prototype's Minimal alt + Tweaks panel are intentionally NOT built.
 * (D1, 2026-06-08: the hero search was removed; it was non-functional.)
 *
 * The nav chrome lives in AppShell (X.6.2). Tile visibility is server-aligned:
 * each tile gates on a capability predicate mirroring the server matrix; the
 * linked surfaces enforce their own authorization. Tenant-neutral: the
 * greeting name comes from the authenticated principal; tile labels/URLs
 * resolve through the registry + Era-A config fill (hubTiles.ts).
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  ArrowRight,
  ArrowUpRight,
  Sunrise,
  Sun,
  Sunset,
  Moon,
  type LucideIcon,
} from 'lucide-react';
import { useTenant } from '@/lib/auth';
import {
  HUB_SECTIONS,
  tileVisible,
  resolveTile,
  type HubTile as HubTileDef,
  type ResolvedHubTile,
} from '@/lib/hubTiles';
import { HubAnnouncements } from '@/components/hub/HubAnnouncements';
import { HubMyTasksRail } from '@/components/hub/HubMyTasksRail';
import { HubQuote } from '@/components/hub/HubQuote';
import { HubSection } from '@/components/hub/HubSection';
import { WelcomeMoment } from '@/components/hub/WelcomeMoment';
import { HubChip } from '@/components/hub/HubTile';
import { BrandWatermark } from '@/components/primitives/BrandWatermark';
import { useRecentTools, relativeTime } from '@/hooks/useRecentTools';
import { endSentence } from '@/lib/sentence';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { bakeOffLeagueOn, feedbackContactEmail, feedbackMailto, welcomeDay } from '@/lib/tenantConfig';
import { firstWord, signedInName } from '@/lib/signedInName';
import { orientationDate } from '@/lib/cmHome';
import { practiceTodayIso } from '@/lib/practiceDate';

const ALL_TILES: readonly HubTileDef[] = HUB_SECTIONS.flatMap((s) => s.tiles);

function greetingForHour(hour: number): string {
  if (hour < 5) return 'Hello';
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function greetingIconForHour(hour: number): LucideIcon {
  if (hour < 5) return Moon;
  if (hour < 12) return Sunrise;
  if (hour < 17) return Sun;
  return Sunset;
}

interface RecentResolved {
  resolved: ResolvedHubTile;
  icon: HubTileDef['icon'];
  when: string;
  onActivate: () => void;
}

function RecentToolCard({ resolved, icon, when, onActivate }: RecentResolved): ReactNode {
  const Arrow = resolved.isExternal ? ArrowUpRight : ArrowRight;
  const cls =
    'group flex items-center gap-3 rounded-card bg-paper px-[15px] py-[13px] text-inherit no-underline shadow-sh2 transition-[box-shadow,transform] duration-[var(--dur-slow)] ease-quiet hover:-translate-y-[3px] hover:shadow-sh3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';
  const inner = (
    <>
      <HubChip icon={icon} size={40} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[0.84375rem] font-semibold text-sage-800">{resolved.label}</span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[0.71875rem] text-ink-soft">
          <Clock size={12} aria-hidden="true" className="shrink-0" />
          <span className="truncate">{when}</span>
        </span>
      </span>
      <Arrow size={15} aria-hidden="true" className="shrink-0 text-sage-300 transition-colors group-hover:text-sage-400" />
    </>
  );
  return resolved.isExternal ? (
    <a
      data-testid={`hub-recent-${resolved.id}`}
      href={resolved.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onActivate}
      className={cls}
    >
      {inner}
    </a>
  ) : (
    <Link data-testid={`hub-recent-${resolved.id}`} to={resolved.href} onClick={onActivate} className={cls}>
      {inner}
    </Link>
  );
}

export function Hub(): ReactNode {
  const { principal, whoami, tenantSlug } = useTenant();
  const { data: tenantConfig } = useTenantConfig(tenantSlug);
  const feedbackEmail = feedbackContactEmail(tenantConfig);
  const bakeOffOn = bakeOffLeagueOn(tenantConfig);
  const { entries, record } = useRecentTools();

  const now = new Date();
  const first = firstWord(signedInName(whoami, principal));
  const greeting = greetingForHour(now.getHours());
  const greetingText = first ? endSentence(`${greeting}, ${first}`) : `${greeting}.`;
  // The greeting follows the person's own hour; the date is the practice's
  // day, the one My Clients and the task rail beside it read.
  const dateText = orientationDate(practiceTodayIso(now));
  const GreetIcon = greetingIconForHour(now.getHours());

  // "Jump back in": most-recent tiles the user can still see, newest first.
  const recent: RecentResolved[] = entries
    .map((e) => {
      const tile = ALL_TILES.find((t) => t.id === e.id);
      if (!tile || !tileVisible(tile, whoami)) return null;
      return {
        resolved: resolveTile(tile, tenantSlug),
        icon: tile.icon,
        when: relativeTime(e.ts),
        onActivate: () => record(tile.id),
      } satisfies RecentResolved;
    })
    .filter((r): r is RecentResolved => r !== null)
    .slice(0, 4);

  return (
    <div data-testid="hub-main">
      <WelcomeMoment
        tenantSlug={tenantSlug}
        day={welcomeDay(tenantConfig)}
        ready={whoami !== null && tenantConfig !== undefined}
      />
      {/* Warm hero: full-bleed forest band with a faint dove watermark. */}
      <section className="relative overflow-hidden bg-sage-800 text-cream">
        <BrandWatermark />
        <span
          aria-hidden="true"
          className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-warm-400/30 to-transparent"
        />
        <div className="relative mx-auto max-w-[1160px] px-5 py-[30px] md:px-10 md:py-[52px]">
          <div className="mb-3 inline-flex items-center gap-2 text-[0.71875rem] font-semibold uppercase tracking-[0.16em] text-warm-400 md:mb-4">
            <GreetIcon size={15} aria-hidden="true" />
            {dateText}
          </div>
          <h1 className="font-display text-[1.625rem] md:text-[2.75rem] font-semibold leading-[1.04] tracking-[-0.01em] text-cream">
            {greetingText}
          </h1>
          <div className="mt-3 md:mt-4">
            <HubQuote variant="hero" />
          </div>
        </div>
      </section>

      {/* Two-column body (single column < md). On mobile, announcements lead
          (order-1) right under the hero; on desktop they sit in the right
          sticky column (order-2) with the tools on the left. */}
      <div className="mx-auto mt-7 grid max-w-[1160px] grid-cols-1 items-start gap-7 px-5 md:mt-9 md:grid-cols-[1fr_340px] md:gap-9 md:px-10">
        <div className="order-2 md:order-1">
          {/* The left column is the launcher: Jump back in, then the tool
              sections. (The personal agenda relocated to the Workspace My Tasks
              tab; a compact glance lives in the right rail.) */}
          {recent.length > 0 && (
            <div data-testid="hub-jump-back-in" className="mb-9">
              <div className="mb-4 inline-flex items-center gap-2 text-[0.71875rem] font-semibold uppercase tracking-[0.16em] text-warm-400">
                <span aria-hidden="true" className="h-px w-[22px] bg-warm-400" />
                Jump back in
              </div>
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                {recent.map((r) => (
                  <RecentToolCard key={r.resolved.id} {...r} />
                ))}
              </div>
            </div>
          )}

          <div className="space-y-9">
            {HUB_SECTIONS.map((section) => {
              const visibleTiles = section.tiles.filter((tile) => tileVisible(tile, whoami));
              if (visibleTiles.length === 0) return null;
              return (
                <HubSection
                  key={section.id}
                  section={section}
                  visibleTiles={visibleTiles}
                  slug={tenantSlug}
                  onTileActivate={record}
                />
              );
            })}
          </div>
        </div>

        <aside className="order-1 md:order-2 md:sticky md:top-[84px] space-y-6">
          {/* The compact My Tasks glance leads the rail; the full agenda lives in
              the Workspace My Tasks tab. Celebrations (phase-d2) will slot below
              this, above announcements. Empty rail cards hide; My Tasks always
              shows (it carries the inline add). */}
          <HubMyTasksRail />
          <HubAnnouncements />
        </aside>
      </div>
      <footer
        data-testid="hub-footer"
        className="mx-auto mt-10 flex max-w-[1160px] flex-wrap items-center gap-x-3 gap-y-2 px-5 pb-[calc(7rem+env(safe-area-inset-bottom))] text-sm text-ink-soft min-[760px]:pb-10 md:px-10"
      >
        {tenantConfig?.display_name && <span className="min-w-0 break-words">{tenantConfig.display_name}</span>}
        {feedbackEmail && (
          <a className="rounded-base underline underline-offset-4 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400" href={feedbackMailto(feedbackEmail)}>
            Feedback
          </a>
        )}
        {bakeOffOn && (
          <Link
            data-testid="hub-bake-off-link"
            to={`/t/${tenantSlug}/bake-off`}
            className="rounded-base underline underline-offset-4 hover:text-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            🧁 Bake Off League 🧁
          </Link>
        )}
      </footer>
    </div>
  );
}
